// Owns the "first login only" state for the Insights intro banner + guided tour.
// Persisted per-user in localStorage (per-browser by design): one key covers both
// the banner and the tour, so each user sees the onboarding once, then never again
// — unless an admin resets it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';

const KEY_PREFIX = 'bluepill_insights_intro_v1_seen_';

export const onboardingKey = (userId?: number): string => `${KEY_PREFIX}${userId ?? 'anon'}`;

const readSeen = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export interface InsightOnboarding {
  showBanner: boolean;
  tourActive: boolean;
  startTour: () => void;
  dismiss: () => void;
  finishTour: () => void;
  reset: () => void;
}

// `ready` = a visual answer is actually on screen; we only announce the new UI once
// the user can see it.
export function useInsightOnboarding(ready: boolean): InsightOnboarding {
  const { user } = useAuth();
  const key = useMemo(() => onboardingKey(user?.id), [user?.id]);

  const [seen, setSeen] = useState<boolean>(() => readSeen(key));
  const [tourActive, setTourActive] = useState(false);

  // Re-evaluate when the signed-in user changes (different account, same browser).
  useEffect(() => {
    setSeen(readSeen(key));
  }, [key]);

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(key, 'true');
    } catch {
      /* ignore storage failures — onboarding is non-critical */
    }
    setSeen(true);
  }, [key]);

  const startTour = useCallback(() => {
    setTourActive(true);
    markSeen();
  }, [markSeen]);

  const dismiss = useCallback(() => markSeen(), [markSeen]);

  const finishTour = useCallback(() => setTourActive(false), []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setSeen(false);
    setTourActive(false);
  }, [key]);

  return {
    showBanner: ready && !seen,
    tourActive,
    startTour,
    dismiss,
    finishTour,
    reset,
  };
}
